import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Zap } from "lucide-react";
import {
  productImageUrl,
  productImageSrcSet,
  applyImageFallback,
} from "./productImage";
import { slugify } from "./slugify";

// Two headline specs are enough to separate one SKU from the next in a grid.
// Brand is pulled out separately as the eyebrow, so it is skipped here.
const headlineSpecs = (details = {}) =>
  Object.entries(details)
    .filter(([key]) => key !== "Brand" && key !== "About this item")
    .slice(0, 2);

/**
 * The catalogue's product tile. Used by the category, search and all-products
 * grids, so any change here lands everywhere at once.
 *
 * Renders an anchor rather than a click handler: shoppers expect to be able to
 * middle-click a product into a new tab, and crawlers need a real href to
 * follow.
 */
const ProductCard = ({ item, category, page }) => {
  if (!item) return null;

  const to = `/category/${slugify(category)}/${slugify(item.name)}${
    page ? `?page=${page}` : ""
  }`;
  const specs = headlineSpecs(item.details);
  const brand = item.details?.Brand;

  return (
    <Link
      to={to}
      className="group relative flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white
                 transition-all duration-200 hover:-translate-y-0.5 hover:border-gray-300
                 hover:shadow-[0_12px_28px_-18px_rgba(15,23,42,0.55)]
                 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2
                 focus-visible:outline-[var(--website-primary-color)]"
    >
      {item.bestSeller && (
        <span
          className="absolute left-2.5 top-2.5 z-10 inline-flex items-center gap-1 rounded
                     bg-amber-400 px-1.5 py-0.5 text-[0.6rem] font-bold uppercase
                     tracking-wide text-gray-900"
        >
          <Zap size={10} strokeWidth={3} />
          Bestseller
        </span>
      )}

      {/* Fixed-height stage. The stage carries the box; the image inside is
          bounded by it but keeps its own width and height, so its rendered
          dimensions always match its natural ratio. Forcing `h-full w-full`
          onto the image instead — even with object-contain — makes the layout
          box a square-ish shape the photo does not have, which is what image
          aspect-ratio audits measure and flag. */}
      <div className="flex h-40 w-full items-center justify-center border-b border-gray-100
                      bg-[#f8fafc] p-3 sm:h-44">
        <img
          src={productImageUrl(item.image)}
          srcSet={productImageSrcSet(item.image)}
          // Widest this tile ever gets is the 2-column phone layout, at roughly
          // half the viewport; from `sm` up the grid tightens to 3-6 columns and
          // the tile settles around 210-260px. Stated in that order because the
          // browser picks using the first matching condition.
          sizes="(max-width: 639px) 50vw, 260px"
          alt={`${item.name} — ${category}`}
          // No width/height attributes: the catalogue's product photos are all
          // different ratios, and stating one ratio for all of them is exactly
          // the mismatch the audit looks for. The stage above reserves the
          // space instead, so leaving these off costs no layout stability.
          loading="lazy"
          decoding="async"
          className="max-h-full max-w-full object-contain transition-transform duration-300
                     group-hover:scale-[1.04]"
          onError={applyImageFallback}
        />
      </div>

      <div className="flex flex-1 flex-col p-3">
        {brand && (
          <span className="text-[0.65rem] font-bold uppercase tracking-[0.08em] text-gray-400">
            {brand}
          </span>
        )}

        {/* Clamped to two lines so every card in a row lines up. */}
        <h3
          className="mt-0.5 line-clamp-2 min-h-[2.5rem] text-sm font-semibold leading-snug
                     text-gray-900 group-hover:text-[var(--website-primary-color)]"
        >
          {item.name}
        </h3>

        {specs.length > 0 && (
          <dl className="mt-2 space-y-0.5">
            {specs.map(([key, value]) => (
              <div key={key} className="flex gap-1.5 text-[0.7rem] leading-tight">
                <dt className="shrink-0 text-gray-400">{key}</dt>
                <dd className="truncate font-medium text-gray-600">{value}</dd>
              </div>
            ))}
          </dl>
        )}

        <div className="mt-auto flex items-center justify-between gap-2 pt-3">
          <span className="text-xs font-semibold text-gray-900">
            Price on request
          </span>
          <span
            className="inline-flex items-center gap-1 rounded-md bg-[var(--website-primary-color)]
                       px-2.5 py-1.5 text-[0.7rem] font-semibold text-white
                       transition-colors group-hover:bg-[#22304f]"
          >
            Get quote
            <ArrowRight size={12} />
          </span>
        </div>
      </div>
    </Link>
  );
};

export default ProductCard;
